import styled from 'styled-components';
import { ReactSVG, Props as ReactSVGProps } from 'react-svg';
import React, { ImgHTMLAttributes } from 'react';
import { css } from 'styles/styled-components';
import withStyledComponent from 'utils/withStyledComponent';

interface StyledSVGProps extends ReactSVGProps {
  renderAsImg?: boolean;
  renderAsImage?: boolean;
  imgAttr?: ImgHTMLAttributes<HTMLImageElement>; // Allows passing all ImgHTMLAttributes
  alt?: string;
  className?: string;
  as?: string; // Add the 'as' property
  rotate?: string | number;
  styles?: ReturnType<typeof css>;
}

// Hàm hỗ trợ tạo CSS transform: rotate
const rotateStyle = (rotate: string | number | undefined) => {
  if (rotate) {
    const value = typeof rotate === 'number' ? `${rotate}deg` : rotate;
    return css`
      transform: rotate(${value});
      > div > svg,
      > div > img {
        transform: rotate(${value});
      }
    `;
  }
  return '';
};

const commonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  > div {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
  &.relative-position {
    position: relative;
    left: -6px;
  }
`;

const SVGIconWrapper = styled.div<Pick<StyledSVGProps, 'rotate'>>`
  ${commonStyles}

  ${({ styles }) => styles}
  /* Áp dụng rotate cho toàn bộ wrapper */
  ${({ rotate }) => rotateStyle(rotate)}
`;

// sc6: styled(ReactSVG) giao với ~500-prop union của ReactSVG làm type nổ (TS2589/2590).
// Cast kết quả về FC<StyledSVGProps> để thu gọn union — thuần type, không đổi runtime.
const StyledSVG = styled(ReactSVG)<StyledSVGProps>`
  ${({ as }) => as !== 'img' && commonStyles}
  /* Áp dụng rotate cho StyledSVG */
  ${({ rotate }) => rotateStyle(rotate)}
` as unknown as React.FC<StyledSVGProps>;

// Component for rendering SVG as an image with all passed ImgHTMLAttributes
const ImgStyledSVG: React.FC<StyledSVGProps> = ({ imgAttr, ...props }) => {
  if (props.as === 'img' && imgAttr) {
    // Để cho phép rotate, ta áp dụng rotateStyle ngay trên thẻ img
    const style =
      typeof props.rotate !== 'undefined'
        ? { transform: `rotate(${typeof props.rotate === 'number' ? `${props.rotate}deg` : props.rotate})` }
        : {};
    // Directly render an img tag when as="img" and imgAttr is provided
    return <img src={props.src} {...imgAttr} className={props.className} style={{ ...imgAttr.style, ...style }} />;
  }
  // For SVG rendering, continue using StyledSVG without imgAttr
  return <StyledSVG {...props} />;
};

// Component for rendering SVG with optional image styling and attributes
const StyledSVGWithImg: React.FC<StyledSVGProps> = ({ className, imgAttr, ...props }) => (
  <SVGIconWrapper className={className} rotate={props.rotate} styles={props.styles}>
    <div>
      <ImgStyledSVG as="img" {...props} imgAttr={imgAttr} className="injected-svg" />
    </div>
  </SVGIconWrapper>
);

/**
 * SVGIcon Component
 *
 * This component is designed to render SVG icons with optional enhancements. It supports rendering as a traditional SVG or as an image tag (`<img>`), depending on the provided props. Additional image attributes can be passed for more control over the rendering when using the `<img>` tag. It also supports custom styling through styled-components.
 *
 * Props:
 * - `renderAsImg` (boolean): Determines if the SVG should be rendered as an `<img>` tag. Defaults to true.
 * - `className` (string): Optional. Custom class name to apply to the component for styling.
 * - `imgAttr` (ImgHTMLAttributes<HTMLImageElement>): Optional. Attributes to pass to the `<img>` tag when `renderAsImg` is true.
 * - Additional props supported by `ReactSVGProps`.
 *
 * Returns:
 * - A React component that renders an SVG icon, either as a traditional SVG or as an image, with optional custom styling.
 */

// Enhanced SVGIcon component that can receive and pass all ImgHTMLAttributes
const SVGIcon: React.FC<StyledSVGProps> = ({ renderAsImg = true, className, imgAttr, ...props }) => {
  return renderAsImg ? (
    <StyledSVGWithImg className={className} imgAttr={imgAttr} {...props} />
  ) : (
    <StyledSVG {...props} className={className} />
  );
};

export default withStyledComponent(SVGIcon);
